import React, { useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import { Bot, Zap, Globe, TrendingUp, Cpu, Rocket, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { ContactSection } from './ContactSection';
import { Footer } from './Footer';
import { scrollToElement } from '../utils/scroll';

const WebsitesPage: React.FC = () => {
  const contactRef = useRef<HTMLDivElement>(null);

  const handleGetStarted = () => {
    if (contactRef.current) {
      scrollToElement(contactRef.current);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <Toaster position="top-center" />
      
      {/* Navigation */}
      <nav className="p-6">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Bot className="w-8 h-8 text-yellow-400" />
            <span className="text-xl font-bold">Lead Wealth A.I.</span>
          </div>
          <a 
            href="/" 
            className="text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-8 pb-12">
        {/* Hero Section */}
        <section className="text-center mb-20 pt-12">
          <div className="flex justify-center items-center mb-8">
            <Globe className="text-yellow-400 mr-4 animate-float" size={48} />
            <Bot className="text-purple-400 mr-4 animate-float" size={48} />
            <Sparkles className="text-blue-400 animate-float" size={48} />
          </div>
          
          <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-yellow-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
            AI-Powered Web Solutions
          </h1>
          
          <h2 className="text-3xl mb-8 text-gray-200">
            Intelligent Websites That Drive Business Growth
          </h2>
          
          <p className="text-xl mb-12 max-w-4xl mx-auto text-gray-300 leading-relaxed">
            Transform your digital presence with cutting-edge AI technology. We create intelligent websites that leverage artificial intelligence to accelerate business growth, automate processes, and deliver transformative digital solutions.
          </p>
          
          <button 
            onClick={handleGetStarted}
            className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Start Your AI Transformation
          </button>
        </section>

        {/* Market Opportunity */}
        <section className="mb-20">
          <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <TrendingUp className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-3xl font-bold mb-4">The AI Revolution in Web Development</h3>
              <p className="text-xl text-gray-300">
                We operate in a rapidly expanding market where AI is transforming how businesses approach web development and digital growth.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">2025</div>
                <p className="text-gray-300">Latest AI tools transforming web development</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-400 mb-2">∞</div>
                <p className="text-gray-300">Unlimited growth potential with AI automation</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">24/7</div>
                <p className="text-gray-300">AI-powered systems working around the clock</p>
              </div>
            </div>
          </div>
        </section>

        {/* Key Services */}
        <section className="mb-20">
          <h3 className="text-4xl font-bold text-center mb-16">Our AI-Powered Services</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* AI-Driven Website Development */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="flex items-center mb-6">
                <Globe className="w-12 h-12 text-blue-400 mr-4" />
                <h4 className="text-2xl font-bold">AI-Driven Website Development</h4>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Harness the power of the latest AI tools that are revolutionizing web development in 2025. Our intelligent websites adapt, learn, and optimize themselves for maximum performance and user engagement.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>AI-powered design optimization</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Intelligent content generation</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Automated user experience enhancement</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Real-time performance analytics</span>
                </li>
              </ul>
            </div>

            {/* Business Growth Automation */}
            <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="flex items-center mb-6">
                <Rocket className="w-12 h-12 text-purple-400 mr-4" />
                <h4 className="text-2xl font-bold">Business Growth Automation</h4>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Implement AI to automate critical business processes, provide actionable insights, and improve decision-making. Transform your operations with intelligent automation that scales with your business.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Automated lead generation and nurturing</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Intelligent customer segmentation</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Predictive analytics and insights</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Automated marketing campaigns</span>
                </li>
              </ul>
            </div>

            {/* Enterprise AI Integration */}
            <div className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="flex items-center mb-6">
                <Cpu className="w-12 h-12 text-yellow-400 mr-4" />
                <h4 className="text-2xl font-bold">Enterprise AI Integration</h4>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Help companies leverage generative AI to reinvent aspects of their marketing and business functions. Seamlessly integrate AI capabilities into existing workflows and systems.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Custom AI model development</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Generative AI implementation</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Legacy system AI enhancement</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>AI strategy consulting</span>
                </li>
              </ul>
            </div>

            {/* Managed Hosting */}
            <div className="bg-gradient-to-br from-green-600/20 to-teal-600/20 backdrop-blur-sm rounded-xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="flex items-center mb-6">
                <Zap className="w-12 h-12 text-green-400 mr-4" />
                <h4 className="text-2xl font-bold">Managed Hosting</h4>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">
                Reliable hosting infrastructure specifically optimized for AI-powered websites. Ensure your intelligent systems run smoothly with our specialized hosting solutions.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>AI-optimized server configurations</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Scalable cloud infrastructure</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>24/7 monitoring and support</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
                  <span>Automated backups and security</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold mb-6">Why Choose AI-Powered Web Solutions?</h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Transform your digital presence with intelligent technology that works smarter, not harder.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Accelerated Growth</h4>
              <p className="text-gray-300">AI-driven insights and automation accelerate your business growth exponentially.</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Intelligent Automation</h4>
              <p className="text-gray-300">Automate complex processes with AI that learns and adapts to your business needs.</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cpu className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Cutting-Edge Technology</h4>
              <p className="text-gray-300">Stay ahead with the latest AI tools and technologies transforming web development.</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Global Scalability</h4>
              <p className="text-gray-300">Build solutions that scale globally with AI-powered infrastructure and optimization.</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">24/7 AI Operations</h4>
              <p className="text-gray-300">Your AI systems work around the clock, optimizing and improving continuously.</p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-xl font-bold mb-3">Future-Ready Solutions</h4>
              <p className="text-gray-300">Build for tomorrow with AI solutions that evolve with emerging technologies.</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center mb-20">
          <div className="bg-gradient-to-r from-purple-600/30 to-blue-600/30 backdrop-blur-sm rounded-2xl p-12 border border-white/10">
            <h3 className="text-4xl font-bold mb-6">Ready to Transform Your Digital Presence?</h3>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join the AI revolution and discover how intelligent web solutions can accelerate your business growth. 
              Let's build something extraordinary together.
            </p>
            <button 
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-4 px-8 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center"
            >
              Get Started Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Contact Section */}
        <div ref={contactRef}>
          <ContactSection />
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default WebsitesPage;